import Info from "@assets/settings/info.svg";
import EmptyState from "@components/screens/EmptyState";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { userManagementApis } from "@services/apis/UserManagement";
import { PaymentMethodsResponseData } from "@services/models/UserManagement/payment-methods";
import React, { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";

interface PaymentMethodSliderProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (success: boolean) => void;
  userId: number;
}

const PaymentMethodSlider: React.FC<PaymentMethodSliderProps> = ({
  isOpen,
  onClose,
  onConfirm,
  userId,
}) => {
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<
    PaymentMethodsResponseData[]
  >([]);

  const fetchPaymentMethods = async () => {
    setIsLoading(true);
    try {
      const paymentMethodsFromApi =
        await userManagementApis.getPaymentMethods(userId);
      setPaymentMethods(paymentMethodsFromApi.data);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      setError(error.message || "An unknown error occurred");
    }
  };

  const getDisplayValue = (paymentMethod: PaymentMethodsResponseData) => {
    const { paymentGroupName, displayFields } = paymentMethod;

    // For UPI Transfer
    if (paymentGroupName === "UPI Transfer") {
      const upiAddress =
        displayFields.find((field) => field.label === "UPI Address")?.value ||
        "";
      const upiHandle =
        displayFields.find((field) => field.label === "UPI Handle")?.value ||
        "";

      if (upiAddress && upiHandle) {
        return `${upiAddress}${upiHandle}`;
      } else if (upiAddress) {
        return upiAddress;
      } else if (upiHandle) {
        return upiHandle;
      }
      return "--";
    }

    // For Bank Transfer
    if (paymentGroupName === "Bank Transfer") {
      const accountNumber = displayFields.find(
        (field) => field.label === "Account Number",
      )?.value;
      return accountNumber || "--";
    }

    // Default case for other payment methods
    return null;
  };

  useEffect(() => {
    if (isOpen) fetchPaymentMethods();
  }, [isOpen, userId]);

  if (error) {
    return (
      <SlidingPanel isOpen={isOpen} onClose={onClose}>
        <div className="p-4 text-red-500">{error}</div>
      </SlidingPanel>
    );
  }

  return (
    <div className="flex flex-col px-[16px] bg-bg-base ">
      {isLoading ? (
        /* ---------- Skeleton ---------- */
        <div className="">
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              className="flex flex-row h-[35px] justify-between items-center">
              <div className="flex flex-row w-1/2 justify-between">
                <Skeleton width="120px" height="20px" className="mr-4" />
                <Skeleton width="30px" height="15px" />
              </div>
              <div className="flex">
                <Skeleton width="20px" height="20px" />
              </div>
            </div>
          ))}
        </div>
      ) : paymentMethods && paymentMethods.length > 0 ? (
        paymentMethods.map((method, index) => (
          <div
            key={index}
            className={`flex flex-row py-[12px] justify-between items-center 
      ${index !== paymentMethods.length - 1 ? "border-b border-border-subtle" : ""}`}>
            <div className="flex flex-col">
              <span className="text-text-base text-[12px] font-medium">
                {getDisplayValue(method)}
              </span>
              <span className="text-text-base text-[10px] font-normal">
                {method.bankOrAppName}
              </span>
            </div>

            <div className="flex py-[4px] items-center justify-center px-[6px] rounded-sm bg-bg-base text-text-subtle text-[10px] font-medium min-w-[60px]">
              {method.paymentGroupName}
            </div>

            <div className="flex rounded-[4px] bg-bg-button-secondary justify-center items-center h-[20px] w-[20px] shadow-[0px_2px_4px_0px_rgba(0,0,0,0.08)] min-w-[20px] cursor-pointer">
              <img src={Info} alt="Info" />
            </div>
          </div>
        ))
      ) : (
        /* ---------- Empty State ---------- */
        <EmptyState
          text="No payment methods have been added yet"
          subText="No payment methods have been added yet. Go to add new method tab to create a payment method!"
        />
      )}
    </div>
  );
};

export default PaymentMethodSlider;
